/** 
 * @fileoverview Lead Qualification Scoring Utilities and Functions
 * 
 * calculates prospect qualify scores for lead based on response to qualifier questions
 * normalizes/scales score to 0-100 regardless of question count, options or assigned score totals
 */

/**
 * calcQualifierScore:
 * Calculate qualification score as percentage of maximum possible points
 *
 * currently handles two different methods of scoring accounting for two different array formats
 * - index format (command center): correct and intended format. answers[qId] = 2
 * - object format (landing page): legacy format, to be phased out. answers[qId] = {label: "Yes", points: 15}
 *  
 * @param {Object} answers - map of question IDs to selected answer indices
 * @param {Object[]} qualQs - array of qualifier question objects
 * 
 * @returns {number} score from 0-100
 * 
 * @example
 * // index format - preferred and eventual final used format
 * const score = calcQualifierScore(
 *  { q1: 2, q2: 0},                   // user selected index 2 for q1, index 0 for q2
 *  [
 *    { id: "q1", answers: [{points: 0}, {points: 5}, {points: 10}] },
 *    { id: "q2", answers: [{points: 15}, {points: 5}] }
 *  ]
 * );
 * // returns: 100 (earned 25 out of max 25 points)
 * 
 * @example
 * // object format (legacy, to be removed later in the refactor phase)
 * const score = calcQualifierScore(
 *  {q1: {points: 10}, q2: {points: 15} },
 *  questions
 * );
 * 
 */
export function calcQualifierScore(answers, questions) {
    if (!Array.isArray(questions) || questions.length === 0) return 0;
    if (!answers || typeof answers !== 'object') return 0;

    // calculate max possible score  (sum of highest points)
    const maxPossible = questions.reduce((sum, q) => {
        const maxPoints = Math.max(...q.answers.map(a => a.points));
        return sum + maxPoints;
    }, 0);
    
    if (maxPossible === 0) return 0;

    // calculate actual score
    let earned = 0;
    questions.forEach(q => {
        const answerData = answers[q.id];

        if (answerData === undefined || answerData === null) {
            /* unanswered question - contributes 0 points
            a guard for an admin recalculating and saving a score that was present before the addition of a newer question (and thus could not answer it).
            */
            return;
        }

        // detect format and extract points
        let points = 0;

        if (typeof answerData === 'number') {
            // INDEX FORMAT: answerData is index into q.answers array
            if (q.answers[answerData] !== undefined) {
                points = q.answers[answerData].points;
            }        
        }

        else if (typeof answerData === 'object') {
            // OBJECT FORMAT: answerData directly contains the points value
            if (answerData.points !== undefined) {
                points = answerData.points;
            } 
        }

        earned += points;
    });

    // normalize to 0-100 - scaling
    return Math.round((earned / maxPossible) * 100);
} 

/**
 * getScoreCategory:
 * maps lead to 'prospect' category based on score amount, with recommended action
 * 
 * @param {number} score - qualification score (0-100)
 * @returns {{ label: string, color: string, action: string }} - category type + category type details 
 */

export function getScoreCategory(score) {
    if (score >= 75) {
        return {
            label: "HOT PROSPECT",
            color: "#ef4444",
            action: "High priority - Follow up asap."
        };
    }

    else if (score >= 50) {
        return {
            label: "WARM PROSPECT",
            color: "#f59e0b",
            action: "Priority - Follow up within set timeframe."
        }
    }

    else {
        return {
            label: "COLD PROSPECT",
            color: "#4da6ff", 
            action: "Lower Priority - Follow up when available."
        }
    }
}
